import React, { useState } from 'react';
import { useListFireDrillsQuery, useGetLocationPoliciesQuery } from '../../store/api/localApi';
import StartDrillModal from './StartDrillModal';
import { ScopedLink } from '../ScopedLink';
import { getChipConfig } from '../../utils/complianceChip';
import { PolicyStatus } from '../../types';

interface ComplianceCardProps {
  locationId: string;
}

const ComplianceCard: React.FC<ComplianceCardProps> = ({ locationId }) => {
  const { data, isLoading } = useListFireDrillsQuery({ locationId, limit: 10 });
  const cadence = data?.cadence;
  const recentDrills = data?.drills ?? [];

  // Policy Vault's own front door (PolicyVault.tsx) is buried behind a
  // collapsed "Compliance" nav section by default (Layout.tsx — sections
  // collapse-by-default to keep the sidebar short, a deliberate choice we're
  // not overriding). This row is a second, always-visible entry point on the
  // dashboard, not a replacement for the nav link.
  const { data: policyData, isLoading: isPolicyLoading } = useGetLocationPoliciesQuery(locationId, {
    skip: !locationId,
  });
  const policyChecklistRows = (policyData?.register ?? []).filter((r) => !r.isCustom);
  const policiesInVault = policyChecklistRows.filter(
    (r) => r.status === PolicyStatus.ACTIVE
  ).length;

  const [modalOpen, setModalOpen] = useState(false);

  const chip = cadence ? getChipConfig(cadence.isCompliant, cadence.daysInMonthRemaining) : null;

  return (
    <div data-testid="compliance-card" className="bg-white shadow rounded-lg p-6 mt-8">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Compliance</h2>

      <div className="flex flex-wrap items-center gap-3">
        {/* Row label */}
        <span className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
          <span aria-hidden="true">🔥</span>
          Fire drills
        </span>

        {/* Status chip */}
        {isLoading ? (
          <span
            data-testid="compliance-fire-drill-chip"
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500"
          >
            Loading…
          </span>
        ) : chip ? (
          <span
            data-testid="compliance-fire-drill-chip"
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${chip.classes}`}
          >
            {chip.label}
          </span>
        ) : (
          <span
            data-testid="compliance-fire-drill-chip"
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-400"
          >
            No data
          </span>
        )}

        {/* Start drill button */}
        <button
          type="button"
          data-testid="compliance-fire-drill-start"
          onClick={() => setModalOpen(true)}
          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          Start drill
        </button>

        {/* View log link */}
        <ScopedLink
          to="/fire-drills"
          data-testid="compliance-fire-drill-view-log"
          className="text-sm font-medium text-primary-600 hover:text-primary-500"
        >
          View log
        </ScopedLink>
      </div>

      {/* Classes not drilled this month */}
      {cadence && cadence.classesNotDrilledThisMonth.length > 0 && (
        <p className="mt-2 text-sm text-gray-500">
          Not drilled this month:{' '}
          <span className="font-medium text-gray-700">
            {cadence.classesNotDrilledThisMonth.map((c) => c.name).join(', ')}
          </span>
        </p>
      )}

      {/* Policy Vault row. Deliberately neutral — the vault's own page treats
          an empty slot as normal progress, never a deficit (no red/amber, no
          "X missing" framing — see PolicyVault.tsx), so this chip stays gray
          regardless of the count, matching that tone exactly. */}
      <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-gray-100 pt-4">
        <span className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
          <span aria-hidden="true">📄</span>
          Policy vault
        </span>

        {isPolicyLoading ? (
          <span
            data-testid="compliance-policy-chip"
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500"
          >
            Loading…
          </span>
        ) : (
          <span
            data-testid="compliance-policy-chip"
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600"
          >
            {policiesInVault} of {policyChecklistRows.length} in the vault
          </span>
        )}

        <ScopedLink
          to="/policies"
          data-testid="compliance-policy-view-vault"
          className="text-sm font-medium text-primary-600 hover:text-primary-500"
        >
          View vault
        </ScopedLink>
      </div>

      <StartDrillModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        locationId={locationId}
        recentDrills={recentDrills}
      />
    </div>
  );
};

export default ComplianceCard;
